import * as TabsPrimitive from '@radix-ui/react-tabs';
import {
  ComponentPropsWithoutRef,
  ComponentRef,
  TouchEventHandler,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { cn } from '../../utils/className';

interface SidePanelTabsProps
  extends ComponentPropsWithoutRef<typeof TabsPrimitive.Root> {
  enableSwipe?: boolean;
  tabOrder?: string[];
  swipeThreshold?: number;
  maxVerticalDelta?: number;
}

interface SwipeTabsContextValue {
  enableSwipe: boolean;
  activeValue: string;
  onTabChange: (value: string) => void;
  tabOrder: string[];
  swipeThreshold: number;
  maxVerticalDelta: number;
}

const SwipeTabsContext = createContext<SwipeTabsContextValue | null>(null);

/**
 * SidePanelTabs - Root component for side panel tabs
 *
 * A tabs component styled specifically for use in side panels.
 * Uses Radix UI Tabs primitive for accessibility.
 *
 * @example
 * ```tsx
 * <SidePanelTabs defaultValue="details">
 *   <SidePanelTabList>
 *     <SidePanelTab value="details">Details</SidePanelTab>
 *     <SidePanelTab value="events">Event log</SidePanelTab>
 *   </SidePanelTabList>
 *   <SidePanelTabContent value="details">...</SidePanelTabContent>
 *   <SidePanelTabContent value="events">...</SidePanelTabContent>
 * </SidePanelTabs>
 * ```
 */
const SidePanelTabs = ({
  enableSwipe = false,
  tabOrder,
  swipeThreshold = 60,
  maxVerticalDelta = 80,
  defaultValue,
  value,
  onValueChange,
  children,
  ...props
}: SidePanelTabsProps) => {
  const [internalValue, setInternalValue] = useState(
    () =>
      (typeof defaultValue === 'string' && defaultValue) ||
      tabOrder?.[0] ||
      ''
  );

  const activeValue = typeof value === 'string' ? value : internalValue;

  const handleTabChange = useCallback(
    (nextValue: string) => {
      if (typeof value !== 'string') {
        setInternalValue(nextValue);
      }
      onValueChange?.(nextValue);
    },
    [onValueChange, value]
  );

  const contextValue = useMemo(
    () => ({
      enableSwipe,
      activeValue,
      onTabChange: handleTabChange,
      tabOrder: tabOrder ?? [],
      swipeThreshold,
      maxVerticalDelta,
    }),
    [
      activeValue,
      enableSwipe,
      handleTabChange,
      maxVerticalDelta,
      swipeThreshold,
      tabOrder,
    ]
  );

  return (
    <SwipeTabsContext.Provider value={contextValue}>
      <TabsPrimitive.Root
        value={activeValue}
        onValueChange={handleTabChange}
        {...props}
      >
        {children}
      </TabsPrimitive.Root>
    </SwipeTabsContext.Provider>
  );
};

/**
 * SidePanelTabList - Container for the tab triggers
 *
 * Renders a horizontal list of tabs with a bottom border.
 */
const SidePanelTabList = forwardRef<
  ComponentRef<typeof TabsPrimitive.List>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.List
    ref={ref}
    className={cn(
      'border-border-primary flex items-start gap-4 border-b pl-4',
      className
    )}
    {...props}
  />
));
SidePanelTabList.displayName = 'SidePanelTabList';

/**
 * SidePanelTab - Individual tab trigger button
 *
 * Styled with:
 * - Active state: Blue text with 4px blue bottom border
 * - Inactive state: Secondary text with transparent bottom border
 */
const SidePanelTab = forwardRef<
  ComponentRef<typeof TabsPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Trigger
    ref={ref}
    className={cn(
      // Base styles - matches Figma spacing
      'size-p1 relative inline-flex items-center px-3 py-4 font-medium',
      // Bottom border - always present, changes color and thickness
      '-mb-px border-b-4',
      // Inactive state - secondary text, transparent border
      `data-[state=inactive]:text-secondary
      data-[state=inactive]:border-transparent`,
      // Hover state
      'hover:text-primary',
      // Active state - blue text and blue border
      'data-[state=active]:text-focus data-[state=active]:border-focus',
      // Focus state
      'focus-visible:ring-focus/50 focus:outline-none focus-visible:ring-1',
      // Transition
      'transition-colors',
      className
    )}
    {...props}
  />
));
SidePanelTab.displayName = 'SidePanelTab';

/**
 * SidePanelTabContent - Content panel for each tab
 *
 * Renders the content associated with a tab value.
 */
const SidePanelTabContent = forwardRef<
  ComponentRef<typeof TabsPrimitive.Content>,
  ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, onTouchEnd, onTouchStart, value, ...props }, ref) => {
  const swipeContext = useContext(SwipeTabsContext);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  const handleTouchStart: TouchEventHandler<HTMLDivElement> = (event) => {
    onTouchStart?.(event);

    if (
      !swipeContext?.enableSwipe ||
      swipeContext.activeValue !== value ||
      event.touches.length !== 1
    ) {
      return;
    }

    const firstTouch = event.touches[0];
    touchStartRef.current = { x: firstTouch.clientX, y: firstTouch.clientY };
  };

  const handleTouchEnd: TouchEventHandler<HTMLDivElement> = (event) => {
    onTouchEnd?.(event);

    if (
      !swipeContext?.enableSwipe ||
      swipeContext.activeValue !== value ||
      !touchStartRef.current ||
      event.changedTouches.length !== 1 ||
      swipeContext.tabOrder.length < 2
    ) {
      return;
    }

    const endTouch = event.changedTouches[0];
    const deltaX = endTouch.clientX - touchStartRef.current.x;
    const deltaY = endTouch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    const isHorizontalSwipe = Math.abs(deltaX) > Math.abs(deltaY);
    const passesThreshold = Math.abs(deltaX) >= swipeContext.swipeThreshold;
    const isVerticalIntent = Math.abs(deltaY) > swipeContext.maxVerticalDelta;

    if (!isHorizontalSwipe || !passesThreshold || isVerticalIntent) {
      return;
    }

    const activeIndex = swipeContext.tabOrder.indexOf(swipeContext.activeValue);
    if (activeIndex < 0) {
      return;
    }

    const direction = deltaX < 0 ? 1 : -1;
    const nextIndex = activeIndex + direction;
    const nextValue = swipeContext.tabOrder[nextIndex];

    if (nextValue) {
      swipeContext.onTabChange(nextValue);
    }
  };

  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn('focus:outline-none', className)}
      value={value}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      {...props}
    />
  );
});
SidePanelTabContent.displayName = 'SidePanelTabContent';

export { SidePanelTab, SidePanelTabContent, SidePanelTabList, SidePanelTabs };
